import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { App, Card, Select, Switch, Table, Tag } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { Field, FieldArray, FieldProps, Form, Formik, FormikProps } from 'formik';
import * as Yup from 'yup';

import { Button } from '../../../custom';
import { getRoles } from '../../userManagement/request';
import { getErrorMessage } from '../../../utils/getErrorMessage';
import {
  AdmissionStage,
  AdmissionStagePayload,
  configureAdmissionStages,
  getAdmissionStages,
} from './request';

import styles from './admissionApproval.module.scss';

interface FormStage {
  stageOrder: number;
  stageName: string;
  requiredRole: string[];
  actionLabel: string;
  rejectLabel: string;
  isFinalStage: boolean;
}

interface FormValues {
  stages: FormStage[];
}

const emptyStage = (order: number): FormStage => ({
  stageOrder: order,
  stageName: '',
  requiredRole: [],
  actionLabel: '',
  rejectLabel: '',
  isFinalStage: false,
});

const stageSchema = Yup.object().shape({
  stageName: Yup.string().required('Stage name is required'),
  requiredRole: Yup.array().min(1, 'At least one role is required'),
  actionLabel: Yup.string().required('Action label is required'),
  rejectLabel: Yup.string().required('Reject label is required'),
});

const validationSchema = Yup.object().shape({
  stages: Yup.array().of(stageSchema).min(1, 'At least one stage is required'),
});

const AdmissionApproval = () => {
  const { notification } = App.useApp();
  const queryClient = useQueryClient();

  const { data: stagesData, isLoading: stagesLoading, isError, error } = useQuery({
    queryKey: ['get-admission-stages'],
    queryFn: getAdmissionStages,
  });

  const { data: rolesData, isLoading: rolesLoading } = useQuery({
    queryKey: ['get-all-roles'],
    queryFn: getRoles,
  });

  const configureMutation = useMutation({ mutationFn: configureAdmissionStages });

  const roleOptions = rolesData?.map((r) => ({ label: r.name, value: r.name })) ?? [];
  const existingStages = stagesData?.data ?? [];

  const initialValues: FormValues = {
    stages: [emptyStage(1)],
  };

  const handleCountChange = (
    newCount: number,
    values: FormValues,
    setFieldValue: (field: string, value: any) => void,
  ) => {
    if (newCount < 1) return;
    const current = values.stages.length;
    if (newCount > current) {
      const appended = [...values.stages];
      for (let i = current; i < newCount; i++) {
        appended.push(emptyStage(i + 1));
      }
      setFieldValue('stages', appended);
    } else if (newCount < current) {
      setFieldValue('stages', values.stages.slice(0, newCount));
    }
  };

  const handleSubmit = async (values: FormValues, resetForm: () => void) => {
    const payload: AdmissionStagePayload[] = values.stages.map((s, i) => ({
      ...s,
      stageOrder: i + 1,
      requiredRole: s.requiredRole.join(','),
    }));

    try {
      const res = await configureMutation.mutateAsync(payload);
      notification.success({
        message: 'Success',
        description: res?.message || 'Admission stages configured successfully',
      });
      queryClient.refetchQueries({ queryKey: ['get-admission-stages'] });
      resetForm();
    } catch (err: any) {
      const data = err?.response?.data;
      notification.error({
        message: 'Error',
        description: data?.message || data?.title || JSON.stringify(data?.errors) || 'Failed to configure stages',
      });
    }
  };

  const columns: ColumnsType<AdmissionStage> = [
    {
      key: 'stageOrder',
      title: 'Order',
      dataIndex: 'stageOrder',
      width: 70,
    },
    {
      key: 'stageName',
      title: 'Stage Name',
      dataIndex: 'stageName',
    },
    {
      key: 'requiredRole',
      title: 'Required Role(s)',
      dataIndex: 'requiredRole',
      render: (val: string) => (
        <>
          {val?.split(',').map((r) => r.trim()).filter(Boolean).map((r) => (
            <Tag key={r}>{r}</Tag>
          ))}
        </>
      ),
    },
    {
      key: 'actionLabel',
      title: 'Action Label',
      dataIndex: 'actionLabel',
    },
    {
      key: 'rejectLabel',
      title: 'Reject Label',
      dataIndex: 'rejectLabel',
    },
    {
      key: 'isFinalStage',
      title: 'Final Stage',
      dataIndex: 'isFinalStage',
      render: (val: boolean) => (
        <span className={`${styles.badge} ${val ? styles.badgeTrue : styles.badgeFalse}`}>
          {val ? 'Yes' : 'No'}
        </span>
      ),
    },
    {
      key: 'hasUsersAssigned',
      title: 'Users Assigned',
      dataIndex: 'hasUsersAssigned',
      render: (val: boolean) => (
        <span className={`${styles.badge} ${val ? styles.badgeTrue : styles.badgeFalse}`}>
          {val ? 'Yes' : 'No'}
        </span>
      ),
    },
  ];

  return (
    <main className={styles.page}>
      <section className="space-between">
        <h3>Admission Approval Stages</h3>
      </section>

      {/* ── Configure ── */}
      <Card bordered={false} className={styles.card}>
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={(values, { resetForm }) => handleSubmit(values, resetForm)}
          enableReinitialize
        >
          {({ values, setFieldValue, errors, touched }: FormikProps<FormValues>) => (
            <Form>
              <div className={styles.cardHeader}>
                <h4>Configure Stages</h4>
              </div>

              {/* Count selector */}
              <div className={styles.countRow}>
                <label>Number of Stages</label>
                <input
                  className={styles.countInput}
                  type="number"
                  min={1}
                  value={values.stages.length}
                  onChange={(e) => {
                    const n = parseInt(e.target.value, 10);
                    if (!isNaN(n)) handleCountChange(n, values, setFieldValue);
                  }}
                />
              </div>

              <FieldArray name="stages">
                {({ remove }) => (
                  <div className={styles.stageList}>
                    {values.stages.map((stage, index) => {
                      const base = `stages.${index}`;
                      const stageErrors = (errors.stages?.[index] as any) ?? {};
                      const stageTouched = (touched.stages?.[index] as any) ?? {};

                      return (
                        <div className={styles.stageCard} key={index}>
                          <div className={styles.stageHeader}>
                            <span className={styles.stageOrder}>Stage #{index + 1}</span>
                            <Button
                              type="button"
                              variant="text"
                              text="Remove"
                              disabled={values.stages.length === 1}
                              onClick={() => remove(index)}
                            />
                          </div>

                          <div className={styles.stageBody}>
                            {/* Stage Name */}
                            <Field name={`${base}.stageName`}>
                              {({ field }: FieldProps) => (
                                <div className={styles.field}>
                                  <label>Stage Name</label>
                                  <input {...field} className={styles.input} placeholder="e.g. Officer Review" />
                                  {stageTouched.stageName && stageErrors.stageName && (
                                    <span className={styles.error}>{stageErrors.stageName}</span>
                                  )}
                                </div>
                              )}
                            </Field>

                            {/* Required Role */}
                            <div className={styles.field}>
                              <label>Required Role(s)</label>
                              <Select
                                mode="multiple"
                                placeholder="Select one or more roles"
                                size="large"
                                style={{ width: '100%' }}
                                options={roleOptions}
                                loading={rolesLoading}
                                value={stage.requiredRole}
                                onChange={(val) => setFieldValue(`${base}.requiredRole`, val)}
                                showSearch
                                filterOption={(input, option) =>
                                  (option?.label as string)?.toLowerCase().includes(input.toLowerCase())
                                }
                              />
                              {stageTouched.requiredRole && stageErrors.requiredRole && (
                                <span className={styles.error}>{stageErrors.requiredRole as string}</span>
                              )}
                            </div>

                            {/* Action Label */}
                            <Field name={`${base}.actionLabel`}>
                              {({ field }: FieldProps) => (
                                <div className={styles.field}>
                                  <label>Action Label</label>
                                  <input {...field} className={styles.input} placeholder="e.g. Admit for approval" />
                                  {stageTouched.actionLabel && stageErrors.actionLabel && (
                                    <span className={styles.error}>{stageErrors.actionLabel}</span>
                                  )}
                                </div>
                              )}
                            </Field>

                            {/* Reject Label */}
                            <Field name={`${base}.rejectLabel`}>
                              {({ field }: FieldProps) => (
                                <div className={styles.field}>
                                  <label>Reject Label</label>
                                  <input {...field} className={styles.input} placeholder="e.g. Reject for approval" />
                                  {stageTouched.rejectLabel && stageErrors.rejectLabel && (
                                    <span className={styles.error}>{stageErrors.rejectLabel}</span>
                                  )}
                                </div>
                              )}
                            </Field>

                            {/* Final Stage toggle */}
                            <div className={styles.finalToggle}>
                              <Switch
                                checked={stage.isFinalStage}
                                onChange={(checked) => setFieldValue(`${base}.isFinalStage`, checked)}
                              />
                              <label>Final Stage</label>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </FieldArray>

              <div className={styles.formActions}>
                <Button
                  type="button"
                  variant="text"
                  text="Add Stage"
                  onClick={() =>
                    setFieldValue('stages', [...values.stages, emptyStage(values.stages.length + 1)])
                  }
                />
                <Button
                  type="submit"
                  text={configureMutation.isPending ? 'Saving...' : 'Save Configuration'}
                  isLoading={configureMutation.isPending}
                  disabled={configureMutation.isPending}
                />
              </div>
            </Form>
          )}
        </Formik>
      </Card>

      {/* ── Existing Stages Table ── */}
      <div className={styles.tableSection}>
        <h4>Current Stages</h4>
        <Card bordered={false}>
          <Table
            dataSource={existingStages}
            columns={columns}
            rowKey={(r) => r.id}
            loading={stagesLoading}
            pagination={false}
            scroll={{ x: true }}
            locale={{ emptyText: isError ? getErrorMessage(error) : 'No stages configured yet' }}
          />
        </Card>
      </div>
    </main>
  );
};

export default AdmissionApproval;
